/**
 * shell/useProjection.ts —— 投影状态（**装配面 · main**）：把 WS 下行帧收成一份**共享投影状态**，
 * 供应用壳的各界面消费。
 *
 * 依据：`web/CONTRACT.md` §3（帧**三型**：event／snapshot／command_ack；载荷＝投影 DTO，不由前端推断）＋
 *       §4（游标与回放由 `transport` 承担，本件不重复实现）。
 *
 * 纪律：
 *  · 帧的解析/去重/游标**全在 `transport/`**（本件只收 `onFrame` 的结果，不解析文本）；
 *  · **界面归属靠声明**：装配项 `slots[].pointer === "views/<界面名>.yaml"` ⇒ 归该界面；
 *    该指针即**后端映射后的 key**（C3 映射归后端组件；前端**不得自造对应表**、不得把
 *    后端读面 id 反猜成 UI 视图名）；无归属声明的装配**不硬塞给任何界面**（缺席即缺席，不补造值）；
 *  · 会话列表＝宿主注入优先／否则投影读面 `views/sessions.yaml` 装配（行键按声明逐键命中）；
 *  · ★AUF3（L-B1-RUN）：**运行中信号**＝后端 run 态帧（载荷 `pointer === "chat/run"`；帧名仍为
 *    既有声明名）派生的 `state.running`——**只消费、不自算**（前端零推断）；无帧 ⇒ 假。
 *    链路：`App` ⇒ `AppShell.running` ⇒ `Composer`（繁忙期投递＝插话 `steer`／排队）；
 *  · 上行＝**唯一实装通道**（信封 `{kind:"command",text,client_id}`，CONTRACT §2）；未连 ⇒ `false`
 *    （不缓存、不假发）；已连 ⇒ 本地记一条**乐观回执行**（「已受理中」）⇒ 收 `command_ack` 帧
 *    **按 `client_id` 精确**更新为 accepted／rejected／error
 *    （**禁编造助手内容**：本件只记**自己发出的请求**与其**服务端回执**，不生成任何助手文本）；
 *    ★A-031（20261006）：关联键＝**客户端关联 id**（`client_id`，值即回执行 `localId`）——
 *    删掉原「按文本匹配最老 pending」口径（同文本连发必错配 ⇒＝报障「内容随消息混动」）；
 *    无 `client_id`（空串／缺）的回执 ⇒ **不落位、不造行**（旧形态回执不得被猜配）。
 *    会话归属＝**连接级上下文**（与建连上报 `?session=` 同源同值）⇒ 上行帧恒带线上键 `target`
 *    （后端 `command_ingress.COMMAND_KEYS` 闭集成员；前端调用面名 `sessionId` 由 `buildCommand` 归一，见 `transport/command`）；
 *  · 本件不取数、不投影、不造结构（结构由 C3 投影＋`views/*.yaml` 生成）。
 *  ★SESS2（20261011 · L-PF-SESS-3）：上行面增 **`switchSession`**（会话动作帧，`action:"session.switch"`）——
 *    同一 WS 通道、同一「未连 ⇒ false 不假发」纪律；后端 `SESSION_ACTIONS` 按 `action` 值分派，
 *    而此前全树**零 `session.switch` 上行点**（点存量会话静默）。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sendCommand, sendSessionAction, type CommandMeta } from "../transport/command";
// ★A-044①：异常展示文案唯一拼装口（按错误码分流；文案源＝transport/errorText.ts）
import { formatTransportError } from "../transport/errorText";
// ★F2：附件上行（同一 WS 信封）＋ 回执 FIFO 配对（能力体在 `transport/attach.ts`）
import {
  buildAttachment,
  createAttachQueue,
  isAttachmentAck,
  sendAttachment,
  type AttachRecord,
} from "../transport/attach";
import type { CommandAckFrame, Frame, ViewAssembly } from "../transport/dto";
import { WsClient, type TransportStatus } from "../transport";
import type { ClientHandlers } from "../views/chatStep";
import { factToRow, type ProjectionInput } from "../views/bind";
import { flowRowsFrom, type FlowRow } from "./flowRows";
import { SHELL_DECL } from "./shellDecl";
import type { FlowRowSourceDecl } from "./shellDecl";
import type { SessionItem } from "./types";

export interface ProjectionState {
  status: TransportStatus;
  /** 快照装配（按到达顺序；重连后由新快照整体替换） */
  views: ViewAssembly[];
  /** 事件帧载荷（审批卡等） */
  payloads: Array<Record<string, unknown>>;
  /**
   * ★PFK5（B9 真流式三件）：**暂态流式叠加**——`message_id` ⇒ 该行正文／相位。
   * 纪律：增量帧**只写本表一项**（`views`／`payloads` 引用原样）⇒ **只更新该行**；
   * 快照到达 ⇒ 已入库的定稿叠加**退场**（以库为准，内容对拍，见 `applyProjectionFrame`）。
   */
  streams: Record<string, StreamEntry>;
  /** 上行命令回执行（本地乐观入列 → 服务端回执更新；**只记自己发的**，不编造助手内容） */
  outbox: CommandOutboxEntry[];
  error: string | null;
  /**
   * ★AUF3（L-B1-RUN · 运行中信号）：**由后端 run 态帧派生**（`pointer=chat/run`；前端零推断、
   * 不自造「正在跑」）。真 ⇒ 本回合在跑 ⇒ `App` 原样传 `AppShell.running` ⇒ `Composer` 把
   * 繁忙期投递分流为 **插话（`kind:"steer"`）／排队（入本地队列）**（`shell/useComposerQueue`）。
   * 派生点唯一＝`applyProjectionFrame`（帧面单入口）；无帧 ⇒ 恒 `false`（不假「运行中」）。
   */
  running: boolean;
}

/** 流式叠加项（★PFK5）：该 `message_id` 行的累积正文与相位（暂态，不落库） */
export interface StreamEntry {
  text: string;
  phase: string;
}

/** 流式三相位（与后端 `runtime/executor/loop.py::STREAM_PHASE_*` **逐字同值**） */
export const STREAM_PHASE_IDENTITY = "identity";
export const STREAM_PHASE_DELTA = "delta";
export const STREAM_PHASE_FINAL = "final";
/** A2UI 数据模型增量**消息名**（与后端 `A2UI_STREAM_MESSAGE` 同值；协议轴换轴归 B8-a） */
export const A2UI_UPDATE_DATA_MODEL = "updateDataModel";

/** 上行命令回执行（乐观态 → 回执态；`localId` ＝ **客户端关联 id**，即线上 `client_id`） */
export interface CommandOutboxEntry {
  /** ★A-031：客户端关联 id —— 原样作线上 `client_id` 上行，回执按它**精确落位**（非服务端帧 id） */
  localId: string;
  text: string;
  /** pending＝「已受理中」（乐观）；accepted／rejected／error ＝ 服务端回执三态 */
  status: "pending" | "accepted" | "rejected" | "error";
  /** 服务端回执帧 id（`cmd-<n>`；未到 ⇒ 缺） */
  ackId?: string;
  /** 回执错误码／说明（rejected／error 时；缺即缺） */
  error?: string;
}

export function initialProjectionState(): ProjectionState {
  return { status: "idle", views: [], payloads: [], streams: {}, outbox: [], error: null, running: false };
}

// ── ★AUF3（L-B1-RUN · 运行中信号）run 态帧 ───────────────────────────────────
/**
 * run 态帧的载荷 `pointer`（**后端出帧口单源**＝`runtime/executor/loop.py::RUN_STATE_POINTER`）：
 * 与回复帧（`chat/reply`）同族、同帧型（既声明事件名的 `event` 帧），靠 `pointer` 分流。
 * 状态词＝**A1 run 域逐字**（后端 `RUN_STATE_RUNNING／SUCCEEDED／FAILED／CANCELLED`；前端不自造词）。
 */
export const RUN_POINTER = "chat/run";
export const RUN_IN_FLIGHT = "running";
/** run 终态词（`A1 RUN_TERMINAL_STATES` 中本帧面的三值，含 cancelled＝★CONSTR1_s §7.2）⇒ 运行中信号落假 */
export const RUN_END_STATES: readonly string[] = ["succeeded", "failed", "cancelled"];

/** 帧判别：`views` ⇒ snapshot；`name` ⇒ event；其余 ⇒ command_ack（与 `frameKindOf` 同序） */
function isAckFrame(frame: Frame): frame is CommandAckFrame {
  return !("views" in frame) && !("name" in frame);
}

/** 回执 → 错误说明（`error.code/message` 优先；缺 ⇒ 显式兜底文案，不静默） */
function ackErrorText(ack: CommandAckFrame): string {
  const error = (ack.error ?? {}) as { code?: unknown; message?: unknown };
  const code = typeof error.code === "string" ? error.code : "";
  const message = typeof error.message === "string" ? error.message : "";
  if (code || message) return [code, message].filter(Boolean).join("：");
  return "命令被拒（status=" + ack.status + "）";
}

/** 回执 → 关联键（`client_id`：后端**原值回带**；缺／非串 ⇒ 空串＝显式「无关联」） */
function ackClientId(ack: CommandAckFrame): string {
  return typeof ack.client_id === "string" ? ack.client_id : "";
}

/**
 * ★PFK5：事件帧载荷 → **流式增量**（非增量 ⇒ `null`，走既有 `payloads` 口径）。
 *
 * 认形（与后端 `WiringFrameSink._stream_payload` 同源）：`payload.a2ui.type === "updateDataModel"`
 * ＋ 非空 `payload.message_id`；`op` 三读法——`identity`＝置空建行／`delta`＝该行累积正文
 * （帧带累积值 ⇒ 丢帧自愈）／`final`＝定稿**整体置位**（暂态全丢也回落定稿，不报错、不造内容）。
 */
function incrementOf(payload: Record<string, unknown>): StreamEntry & { id: string } | null {
  const a2ui = payload.a2ui as { type?: unknown; op?: unknown; value?: unknown } | null | undefined;
  const id = payload.message_id;
  if (!a2ui || typeof a2ui !== "object" || a2ui.type !== A2UI_UPDATE_DATA_MODEL) return null;
  if (typeof id !== "string" || id === "") return null;
  const phase = typeof payload.phase === "string" ? payload.phase : STREAM_PHASE_DELTA;
  const text = a2ui.op === "replace"
    ? (typeof a2ui.value === "string" ? a2ui.value : "")   // identity（空）／final（全文）⇒ 整体置位
    : textOf(payload.text);                                // delta ⇒ 帧带的**累积**正文
  return { id, text, phase };
}

/**
 * ★PFK5（B9③ 以库为准）：快照到达 ⇒ **已入库**的定稿叠加退场（**内容对拍**，不按行键猜配——
 * 新树行 `message_id` 恒空、前端按行序兜锚，键对不上）。
 * 库里没有同文（落库被跳过／残）⇒ **保留**该定稿叠加：不报错、不消失、不造假行（B10④ 回落定稿）。
 */
export function confirmBySnapshot(
  streams: Record<string, StreamEntry>,
  views: ViewAssembly[] | undefined,
): Record<string, StreamEntry> {
  const ids = Object.keys(streams);
  if (ids.length === 0) return streams;                  // 无叠加 ⇒ 原样引用（零扰动）
  const inDb = new Set<string>();
  for (const view of views ?? []) {
    const fact = (view.fact ?? {}) as Record<string, unknown>;
    for (const name of ["scope", "text", "content"]) {
      const value = textOf(fact[name]);
      if (value) inDb.add(value);
    }
  }
  const kept: Record<string, StreamEntry> = {};
  for (const id of ids) {
    const entry = streams[id];
    if (entry.phase === STREAM_PHASE_FINAL && !inDb.has(entry.text)) kept[id] = entry;
  }
  return kept;
}

/**
 * 帧 → 状态（同 `chatState.applyFrame` 口径：快照整体替换、事件追加）。
 * ★类K·3（20261007）：**见帧即清**——三分支一律 `error: null`：任一**合法帧**到达即证明
 * 链路活着，撤此前的链路类错误提示（旧口径提示要挂到下一帧，且静默期被看门狗误判后一直悬着）。
 * `command_ack` 帧 ⇒ **按 `client_id` 精确更新**对应回执行（★A-031：删「按文本／最老」匹配——
 * 同文本连发必错配、状态串行＝「内容混动」的根因）；无关联键（空串／缺）或 id 不在本表 ⇒
 * **不落位、不造行**（旧形态回执不得被猜配，保持现纪律）。
 * ★PFK5：流式三态事件帧 ⇒ **只写 `streams` 一项**（`views`／`payloads` 引用**原样**）⇒
 * **增量只更新该行**（其他行对象零重建）；快照帧 ⇒ 暂态叠加按「已入库者退场」收口。
 */
export function applyProjectionFrame(prev: ProjectionState, frame: Frame): ProjectionState {
  if ("views" in frame) {
    return { ...prev, views: frame.views, streams: confirmBySnapshot(prev.streams, frame.views), error: null };
  }
  if (isAckFrame(frame)) {
    const clientId = ackClientId(frame);
    if (clientId === "") return { ...prev, error: null };            // 无关联键 ⇒ 不猜配、不造行
    const index = prev.outbox.findIndex((row) => row.localId === clientId);
    if (index < 0) return { ...prev, error: null };                  // 无对应请求 ⇒ 不伪造行
    const outbox = prev.outbox.slice();
    outbox[index] = {
      ...outbox[index],
      status: frame.status,
      ackId: frame.id,
      ...(frame.status === "rejected" || frame.status === "error" ? { error: ackErrorText(frame) } : {}),
    };
    return { ...prev, outbox, error: null };
  }
  const payload = (frame.payload ?? {}) as Record<string, unknown>;
  // ★PFK5（B9④）：流式三态事件帧 ⇒ **只写 `streams` 一项**（`views`／`payloads` 引用原样）
  //   ⇒ 增量只更新该行 DOM（其余行对象零重建）；非增量帧 ⇒ 走下方既有口径（AUF3 ／ payloads）。
  const increment = incrementOf(payload);
  if (increment) {
    // ★PFK5（B9④）：**就地更新该行** —— 只动 `streams[message_id]` 一项；`views`／`payloads` 引用
    //   **原样**（`{...prev}` 浅拷，未触碰的键仍是同一对象）⇒ 其余行的装配与节点行**零重建**。
    const { id, ...entry } = increment;
    return { ...prev, streams: { ...prev.streams, [id]: entry }, error: null };
  }
  // ★AUF3（L-B1-RUN）：run 态帧 ⇒ **运行中信号派生**（`state.running` 的唯一派生点）。
  //   边界（逐字，禁外推）：本连接＝本会话容器（切会话即重连、游标不跨会话）⇒ 帧序内
  //   run 不交叠，故 `running` 只需「入帧置真／终态帧置假」两位数；后端**两帧成对**出
  //   （run 起一帧 ＋ 本腿收敛一帧，见 `chat_wiring.ingress`）⇒ 不存在只进不出的悬挂。
  const runState = payload.pointer === RUN_POINTER ? textOf(payload.state) : "";
  if (runState === RUN_IN_FLIGHT) {
    return { ...prev, payloads: [...prev.payloads, payload], running: true, error: null };
  }
  if (RUN_END_STATES.includes(runState)) {
    return { ...prev, payloads: [...prev.payloads, payload], running: false, error: null };
  }
  return { ...prev, payloads: [...prev.payloads, payload], error: null };
}

/**
 * 界面名 → 声明指针（`views/<name>.yaml`）。
 * 🔴 这是**后端映射后的 key**（C3：读面视图 → UI 视图的映射归后端组件）；前端**只逐字取用**，
 *    **不得**自造「读面视图 id → UI 视图名」对应表，也不得把读面 id 反猜成 UI 视图名。
 *    漂移由 `tests/frontend.gaps.c3c6.projection.test.tsx`（含 web/src 零「非 UI 视图 pointer」静态扫描）拦下。
 */
export function pointerFor(viewName: string): string {
  return "views/" + viewName + ".yaml";
}

/** 会话源读面 key（后端 C2 读面 `views/sessions.yaml`；装配到该 key 才供左栏会话列表） */
export const SESSIONS_POINTER = "views/sessions.yaml";

/**
 * 快照装配按**后端所给 pointer 逐字分组**（key ⇒ 同 key 装配列表，保持到达序）。
 * 同一装配的重复 pointer 只计一次；无 `views/` 形态 pointer 的装配**不归任何界面**（不猜、不兜）。
 */
export function groupByPointer(views: ViewAssembly[]): Map<string, ViewAssembly[]> {
  const grouped = new Map<string, ViewAssembly[]>();
  for (const view of views) {
    const pointers = new Set<string>();
    for (const slot of view.slots ?? []) {
      if (typeof slot?.pointer === "string" && slot.pointer) pointers.add(slot.pointer);
    }
    for (const pointer of pointers) {
      const bucket = grouped.get(pointer);
      if (bucket) bucket.push(view);
      else grouped.set(pointer, [view]);
    }
  }
  return grouped;
}

/**
 * 会话源（读面装配）→ 会话行：行键**按声明 `sidebar.session_row_source` 逐键命中**取用
 * （① 直给形态 `id/title/state/activity`；② 声明闭集载体键 `key/scope/actor/step`）。
 * 无装配 ⇒ 空数组（**空态**；禁假造条目、禁乐观插行）；候选键皆未命中 ⇒ 该键**缺**（不补造）。
 */
export function sessionsFromViews(views: ViewAssembly[]): SessionItem[] {
  const source = SHELL_DECL.sidebar.session_row_source;
  const pick = (fact: Record<string, unknown>, candidates: string[]): string => {
    for (const key of candidates) {
      const value = textOf(fact[key]);
      if (value) return value;
    }
    return "";
  };
  return views
    .filter((view) => (view.slots ?? []).some((slot) => slot.pointer === SESSIONS_POINTER))
    .map((view) => (view.fact ?? {}) as Record<string, unknown>)
    .map((fact) => {
      const id = pick(fact, source.id);
      const row: SessionItem = { id, title: pick(fact, source.title) || id };
      const state = pick(fact, source.state);
      const activity = pick(fact, source.activity);
      if (state) row.state = state;
      if (activity) row.activity = activity;
      return row;
    })
    .filter((row) => row.id !== "");
}

function textOf(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/**
 * ★PFK5（B9④／B10④）**就地更新该行**：把暂态流式叠加并入中栏节点行。
 *  · 命中行键（＝`message_id`；见 `message_read.yaml::row_carrier.key`）⇒ 只换**该行对象**，
 *    其余行**原样返回同一对象引用** ⇒ React 按 key 复用 ⇒ 其他行 DOM 不重建；
 *  · 无叠加／无命中也无新行 ⇒ **原数组原样返回**（零扰动）；
 *  · 身份先行 ⇒ 库行未到也先建行（节点类取声明 `kind_map["assistant"]`，**不硬编码第二份表**）；
 *  · 定稿（`final`）⇒ 该行 `settled` ＋ 全文（暂态全丢也回落定稿，不报错、不造内容）。
 */
export function withStreamRows(
  flow: FlowRow[],
  streams: Record<string, StreamEntry>,
  decl: FlowRowSourceDecl,
): FlowRow[] {
  const ids = Object.keys(streams);
  if (ids.length === 0) return flow;
  const kind = decl.kind_map["assistant"];
  if (!kind) return flow;                       // 值域外 ⇒ 不冒充节点（目录行不冒充会话内容）
  const stateOf = (entry: StreamEntry): Record<string, unknown> => ({
    role: "assistant",
    status: entry.phase === STREAM_PHASE_FINAL ? "settled" : "streaming",
    text: entry.text,
    blocks: entry.text ? [entry.text] : [],
  });
  let touched = false;
  const out = flow.map((row) => {
    const entry = streams[textOf(row.key)];
    if (!entry) return row;                     // ← 其他行：**同一对象**（不重建）
    touched = true;
    return { ...row, state: { ...row.state, ...stateOf(entry) } };
  });
  const present = new Set(out.map((row) => textOf(row.key)));
  const missing = ids.filter((id) => !present.has(id));
  if (!touched && missing.length === 0) return flow;         // 零命中零新增 ⇒ 原样引用
  for (const id of missing) out.push({ key: id, kind, state: stateOf(streams[id]) });
  return out;
}

export interface UseProjectionResult {
  state: ProjectionState;
  /**
   * ★AUF3（L-B1-RUN）：**运行中信号**（值同 `state.running`；派生点＝`applyProjectionFrame` 的
   * run 态帧分支）——提到调用面单列，`App` 直接透传 `AppShell.running` ⇒ `Composer`
   * （`send⇄steer⇄queue⇄stop` 分流）与 `useComposerQueue`（运行结束依次投）据此判定，**不自算**。
   */
  running: boolean;
  /** 按界面取投影输入（**按后端映射后的 key** 取用；无该 key 的装配 ⇒ 空行，各视图自呈六态） */
  inputFor: (viewName: string) => ProjectionInput;
  /** 装配出现的 pointer key（诊断用；原样＝后端所给，前端不改写） */
  pointerKeys: string[];
  /** 上行命令（**唯一实装通道**：信封 `{kind:"command",text,client_id}`，CONTRACT §2）；未连 ⇒ false（不假发） */
  send: (text: string, meta?: CommandMeta | null) => boolean;
  /**
   * ★SESS2（L-PF-SESS-3 / 方案 §四 B5）：**会话切换上行**——
   * 帧 `{kind:"command",action:"session.switch",target:<id>,client_id:<localId>}`（同一 WS 通道，
   * **零 REST、零第二通道**）；未连 ⇒ false（不假发，不乐观造会话行）。
   * 回执按 `client_id` 精确落位（与 `send` 同口径：本件只记自己发出的请求与它的服务端回执）。
   */
  switchSession: (sessionId: string) => boolean;
  /** 附件上行（**同一 WS 信封**；未连 ⇒ reject，不假发）；回执按到达序 FIFO 配对，失败显式 reject */
  sendAttach: (
    name: string,
    contentType: string,
    bytes: Uint8Array,
    opts?: { timeoutMs?: number },
  ) => Promise<AttachRecord>;
  clientRef: React.MutableRefObject<WsClient | null>;
}

export function useProjection(
  makeClient: (handlers: ClientHandlers) => WsClient,
  enabled = true,
  sessionKey?: string,
  /** ★F3d：`id → 名` 映射（如附件 `file_id → filename`；宿主取回后经 ref 覆盖，**不重建连接**） */
  fileNamesRef?: React.MutableRefObject<Record<string, string>>,
): UseProjectionResult {
  const [state, setState] = useState<ProjectionState>(initialProjectionState);
  const clientRef = useRef<WsClient | null>(null);
  const seqRef = useRef(0);
  /** 会话容器取值器（＝建连上报 `?session=` 的同源同值；上行帧 `target` 由它供） */
  const sessionRef = useRef<string>("");
  /** 附件回执等待队列（后端回执不含请求 id ⇒ 到达序 FIFO；与 outbox 同口径） */
  const attachQueueRef = useRef(createAttachQueue());

  const attach = useCallback((): WsClient => {
    const client = makeClient({
      onFrame: (frame) => {
        // ★F2：附件回执**先**配对等待者；无人等 ⇒ 吞掉（不入投影、左/中栏不因上传造行）
        // ★类K·3：配对命中也走「见帧即清」（帧到＝链路活着的证据；不因早退把链路类错误悬着）
        if (isAttachmentAck(frame) && attachQueueRef.current.settle(frame)) {
          setState((prev) => (prev.error === null ? prev : { ...prev, error: null }));
          return;
        }
        setState((prev) => applyProjectionFrame(prev, frame));
      },
      onStatus: (status) => setState((prev) => ({ ...prev, status })),
      onError: (error) => {
        const shape = error.toShape();
        // A-044①：展示文案按错误码分流、唯一拼装口＝transport/errorText（文案唯一来源＝errorText.ts）
        setState((prev) => ({ ...prev, error: formatTransportError(shape) }));
      },
    });
    clientRef.current = client;
    client.start();
    return client;
  }, [makeClient]);

  useEffect(() => {
    if (!enabled) return undefined;            // 未登录 ⇒ 不建连（读面身份按会话判定）
    const client = attach();
    return () => {
      client.stop();
      clientRef.current = null;
    };
    // ★C-Ⅷ：会话容器随建连上报（`?session=`）⇒ **切会话即重连**，新快照按该会话取行
    // （读面 `$active_session` 绑定）；游标不跨会话复用，故重连取全量快照。
  }, [attach, enabled, sessionKey]);

  // 会话容器取值器随会话同步（装配顺序无关：先于任何 `send` 的赋值在 effect 内完成）
  useEffect(() => { sessionRef.current = sessionKey ?? ""; }, [sessionKey]);

  const byPointer = useMemo(() => groupByPointer(state.views), [state.views]);

  const inputFor = useCallback(
    (viewName: string): ProjectionInput => {
      // **按后端映射后的 key 逐字取用**（前端不别名、不猜映射、不补造归属）
      const mine = byPointer.get(pointerFor(viewName)) ?? [];
      const rows = mine.map((v) => factToRow((v.fact ?? {}) as Record<string, unknown>));
      // ★C-Ⅷ：声明了流行口径、且本界面就是该口径的适用界面 ⇒ 还原**节点行**（中栏流渲染用）
      // ★PFK5：暂态流式叠加**就地并入该行**（`message_id` 命中 ⇒ 只换该行对象）
      const flowDecl = SHELL_DECL.main.flow_row_source;
      const flow = flowDecl && flowDecl.view === viewName
        ? withStreamRows(flowRowsFrom(rows, flowDecl, fileNamesRef?.current), state.streams, flowDecl)
        : undefined;
      return {
        rows,
        ...(flow && flow.length > 0 ? { flowRows: flow } : {}),
        assembly: mine.length > 0 ? mine[mine.length - 1] : undefined,
        pointerValues: {},
      };
    },
    [byPointer, state.streams],
  );

  const pointerKeys = useMemo(() => [...byPointer.keys()].sort(), [byPointer]);

  /**
   * 上行发送：未连 ⇒ `false`（调用方显式处理，**不缓存、不假发、不乐观入列**）；
   * 已连 ⇒ 恰一帧出网（帧带 `client_id`＝本地 `localId`）＋ 本地**乐观入列**一条「已受理中」
   * （`command_ack` 一到即**按 `client_id`** 更新该行）。
   */
  /** 附件上行：一条帧出网 ＋ 挂一个等待者；未连/拒发 ⇒ 显式 reject（不静默假成功） */
  const sendAttach = useCallback((
    name: string,
    contentType: string,
    bytes: Uint8Array,
    opts?: { timeoutMs?: number },
  ): Promise<AttachRecord> => {
    const client = clientRef.current;
    if (!client) return Promise.reject(new Error("未连接：附件上行不发（不假发）"));
    const pending = attachQueueRef.current.wait(opts?.timeoutMs);
    if (!sendAttachment(client, buildAttachment(name, contentType, bytes))) {
      attachQueueRef.current.abort("上行通道拒发（连接态异常）");
    }
    return pending;
  }, []);

  const send = useCallback((text: string, meta?: CommandMeta | null): boolean => {
    const client = clientRef.current;
    if (!client) return false;
    // ★A-031：关联 id 先定后发 —— 帧上 `client_id` 与回执行 `localId` **同值**（回执按它落位）。
    //   发送失败 ⇒ 不提交序位、不入列（不假发）；序位只在真出网后自增。
    const localId = "cmd-local-" + (seqRef.current + 1);
    // 会话归属＝**连接级上下文**：宿主给了就用宿主的，否则取当前会话容器（与建连 `?session=` 同源同值）；
    // 皆无 ⇒ 空串 ⇒ 线上**不出该键**（后端按「无目标会话」处置，不猜会话、不在前端造会话）。
    const ok = sendCommand(client, text, {
      ...(meta ?? {}),
      sessionId: (meta?.sessionId ?? "") || sessionRef.current,
      clientId: localId,
    });
    if (!ok) return false;
    seqRef.current += 1;
    const entry: CommandOutboxEntry = { localId, text, status: "pending" };
    setState((prev) => ({ ...prev, outbox: [...prev.outbox, entry] }));
    return true;
  }, []);

  /**
   * ★SESS2（L-PF-SESS-3 / 方案 §四 B5 · L-PF-SESS-1 的「点击须触发 action」）：
   * **会话切换上行**。与 `send` **同一本书**（localId 取序位、出网成功才自增、乐观入列一条
   * pending、回执按 `client_id` 精确落位）；差别只在帧形状（`action` 族，**无正文**）。
   *
   * 未连 ⇒ `false`（**不假发、不缓存、不入列**）——调用方据此照旧只做本地会话态随动，
   * 不伪造「切换成功」。
   */
  const switchSession = useCallback((sessionId: string): boolean => {
    const client = clientRef.current;
    if (!client) return false;
    const localId = "cmd-local-" + (seqRef.current + 1);
    const ok = sendSessionAction(client, "session.switch", sessionId, { clientId: localId });
    if (!ok) return false;
    seqRef.current += 1;
    setState((prev) => ({ ...prev, outbox: [...prev.outbox, { localId, text: "", status: "pending" }] }));
    return true;
  }, []);


  /**
   * ★AUF3（L-B1-RUN · 运行中信号）：取值口径**单源**＝`state.running`（由后端 run 态帧派生，
   * 见 `applyProjectionFrame`）；本行只是把它提到调用面（`App` 传 `AppShell.running`）。
   * 前端**不自算**忙碌态（零推断：无帧 ⇒ 恒假，不假「运行中」）。
   */
  const running = state.running;

  return useMemo(() => ({ state, running, inputFor, pointerKeys, send, sendAttach, switchSession, clientRef }),
    [state, running, inputFor, pointerKeys, send, sendAttach, switchSession]);
}
